import React, { useState, useRef } from 'react';
import ExportButton from './common/ExportButton';
import { exportElementAsPNG, generateExportFilename } from '../utils/exportUtils';
import { getStoredUser } from '../utils/auth';

// ─── Static benchmark data from client Excel (NexCAR19_SOC_Benchmarking_Dashboard) ───
// NexCAR-19 RWD column is static for now; will be replaced with dynamic API data later.
const benchmarkingData = {
  NHL: {
    headers: [
      {
        id: 'nexcar_rwd',
        name: 'NexCAR19',
        sub: 'Real-World Data',
        isRef: true,
        refLink: 'Data on File',
        citation: 'ImmunoACT Internal Clinical Data on file, 2026.',
      },
      {
        id: 'nexcar_bcj',
        name: 'NexCAR19',
        sub: 'BCJ Published',
        isRef: true,
        refLink: 'PMID: 42463637',
        citation: 'Karulkar, A. et al. Enabling access to talicabtagene autoleucel in relapsed/refractory B-cell malignancies; multicenter real-world evidence on delivery, efficacy and safety. Blood Cancer J. (2026). https://doi.org/10.1038/s41408-026-01569-w',
      },
      {
        id: 'pola',
        name: 'POLA + BR',
        sub: '',
        refLink: 'PMID: 312345',
        citation: 'Sehn L Blood Adv. 2022 Jan 25;6(2):533-543.',
      },
      // ── Hidden per client request (temporary) ──
      // {
      //   id: 'tafa',
      //   name: 'Tafa + Lina',
      //   sub: '',
      //   refLink: 'PMID: 324567',
      //   citation: 'Duell J, et al. Tafasitamab for patients with relapsed or refractory diffuse large B-cell lymphoma: final 5-year efficacy and safety findings in the phase II L-MIND study. Haematologica. 2024 Feb 1;109(2):553-566.',
      // },
      {
        id: 'salvage',
        name: 'Salvage + BMT',
        sub: '',
        refLink: 'Data on File',
        citation: 'Tun AM, et al. Autologous Stem Cell Transplant in Fit Patients With Late Relapsed Diffuse Large B-Cell Lymphoma That Responded to Salvage Chemotherapy. Transplantation and Cellular Therapy, Volume 30, Issue 10, 2024, Pages 1001.e1-1001.e12.',
      },
      {
        id: 'eshap',
        name: 'ESHAP',
        sub: '',
        refLink: 'Data on File',
        citation: 'The Korean Journal of Internal Medicine 2006;21(3):159-164.',
      },
      {
        id: 'rgemox',
        name: 'R-GemOx',
        sub: '',
        refLink: 'PMID: 23753028',
        citation: 'Mounier N, et al. Rituximab plus gemcitabine and oxaliplatin in patients with refractory/relapsed diffuse large B-cell lymphoma who are not candidates for high-dose therapy. A phase II Lymphoma Study Association trial. Haematologica. 2013 Nov;98(11):1726-31. doi: 10.3324/haematol.2013.090597. Epub 2013 Jun 10. PMID: 23753028; PMCID: PMC3815173.',
      },
      // ── Hidden per client request (temporary) ──
      // {
      //   id: 'rdhap',
      //   name: 'R-DHAP',
      //   sub: '',
      //   refLink: 'PMID: 25267740',
      //   citation: 'Crump M, et al. Randomized comparison of GDP vs DHAP chemotherapy before ASCT for relapsed aggressive lymphomas: NCIC-CTG LY.12. J Clin Oncol. 2014 Nov 1;32(31):3490-6.',
      // },
    ],
    groups: [
      { label: 'Primary Refractory + Early Relapse' },
      { label: 'Late Relapse' },
    ],
    data: {
      PFS: {
        'Primary Refractory + Early Relapse': {
          '1Y': { nexcar_rwd: 42, nexcar_bcj: 44, pola: 40, tafa: 42, salvage: null, rdhap: 32, eshap: 32, rgemox: 20 },
          '2Y': { nexcar_rwd: 35, nexcar_bcj: 35, pola: 27, tafa: 24, salvage: null, rdhap: 14, eshap: 32, rgemox: 18 },
          '3Y': { nexcar_rwd: null, nexcar_bcj: null, pola: 20, tafa: 14, salvage: null, rdhap: 6, eshap: 32, rgemox: 16 },
        },
        'Late Relapse': {
          '1Y': { nexcar_rwd: 70, nexcar_bcj: 77, pola: null, tafa: null, salvage: 74, rdhap: null, eshap: null, rgemox: null },
          '2Y': { nexcar_rwd: 70, nexcar_bcj: 72, pola: null, tafa: null, salvage: 63, rdhap: null, eshap: null, rgemox: null },
          '3Y': { nexcar_rwd: null, nexcar_bcj: null, pola: null, tafa: null, salvage: 55, rdhap: null, eshap: null, rgemox: null },
        },
      },
      OS: {
        'Primary Refractory + Early Relapse': {
          '1Y': { nexcar_rwd: 59, nexcar_bcj: 60, pola: 52, tafa: 60, salvage: null, rdhap: 45, eshap: 45, rgemox: 48 },
          '2Y': { nexcar_rwd: 47, nexcar_bcj: 48, pola: 38, tafa: 38, salvage: null, rdhap: 22, eshap: 42, rgemox: 36 },
          '3Y': { nexcar_rwd: null, nexcar_bcj: null, pola: 33, tafa: 25, salvage: null, rdhap: 12, eshap: 42, rgemox: 27 },
        },
        'Late Relapse': {
          '1Y': { nexcar_rwd: 82, nexcar_bcj: 82, pola: null, tafa: null, salvage: 86, rdhap: null, eshap: null, rgemox: null },
          '2Y': { nexcar_rwd: 82, nexcar_bcj: 82, pola: null, tafa: null, salvage: 80, rdhap: null, eshap: null, rgemox: null },
          '3Y': { nexcar_rwd: null, nexcar_bcj: null, pola: null, tafa: null, salvage: 72, rdhap: null, eshap: null, rgemox: null },
        },
      },
    },
    keyInsight: {
      PFS: 'NexCAR19 demonstrates stronger durability and sustainability of PFS from Year 1 to Year 3, while standard-of-care comparators show steeper decline over time.',
      OS: 'NexCAR19 demonstrates stronger year-on-year OS durability from Year 1 to Year 3, while standard-of-care comparators show greater attrition over time.',
    },
  },
  'B-ALL': {
    headers: [
      {
        id: 'nexcar_rwd',
        name: 'NexCAR19',
        sub: 'Real-World Data',
        isRef: true,
        refLink: 'Data on File',
        citation: 'ImmunoACT Internal Clinical Data on file, 2026.',
      },
      {
        id: 'nexcar_bcj',
        name: 'NexCAR19',
        sub: 'BCJ Published',
        isRef: true,
        refLink: 'PMID: 42463637',
        citation: 'Karulkar, A. et al. Enabling access to talicabtagene autoleucel in relapsed/refractory B-cell malignancies; multicenter real-world evidence on delivery, efficacy and safety. Blood Cancer J. (2026). https://doi.org/10.1038/s41408-026-01569-w',
      },
      {
        id: 'sct_ino',
        name: 'SCT + Inotuzumab',
        sub: '',
        strategy: 1,
        refLink: 'Data on File',
        citation: 'Marks D, et al. Outcomes of Allogeneic Stem Cell Transplantation after Inotuzumab Ozogamicin Treatment for Relapsed or Refractory Acute Lymphoblastic Leukemia. Biology of Blood and Marrow Transplantation, 2019; 25, 1720-1729.',
      },
      {
        id: 'allo_blina',
        name: 'Allo SCT + Blinatumomab',
        sub: '',
        strategy: 1,
        refLink: 'Data on File',
        citation: 'Multi-institutional study evaluating clinical outcome with allogeneic hematopoietic stem cell transplantation after blinatumomab in patients with B-cell acute lymphoblastic leukemia: real-world data.',
      },
      // ── Hidden per client request (temporary) ──
      // {
      //   id: 'ph_ino',
      //   name: 'PH-Ino + mini-hyper-CVD',
      //   sub: '',
      //   strategy: 2,
      //   refLink: 'PMID: 567890',
      //   citation: 'Jabbour E, Cancer 2021;127:2025-2038.',
      // },
      {
        id: 'ino',
        name: 'Inotuzumab',
        sub: '',
        strategy: 2,
        refLink: 'DOI: 10.1002/cncr.32116',
        citation: 'Kantarjian HM, et al. Inotuzumab ozogamicin versus standard of care in relapsed or refractory acute lymphoblastic leukemia: Final report and long-term survival follow-up from the randomized, phase 3 INO-VATE study. Cancer, 125: 2474-2487. https://doi.org/10.1002/cncr.32116',
      },
      // ── Hidden per client request (temporary) ──
      // {
      //   id: 'soc_ph',
      //   name: 'SOC PH-Ino + Bosutinib',
      //   sub: 'Strategy 2#',
      //   strategy: 2,
      //   refLink: 'PMID: 789012',
      //   citation: 'Jain N et al. Am J Hematol 2021;96:1000-1007.',
      // },
    ],
    groups: [
      { label: 'Refractory + Relapsed' },
    ],
    data: {
      PFS: {
        'Refractory + Relapsed': {
          '1Y': { nexcar_rwd: 57, nexcar_bcj: 55, sct_ino: 40, allo_blina: 61, ph_ino: 47, ino: 20, soc_ph: 37 },
          '2Y': { nexcar_rwd: 49, nexcar_bcj: 50, sct_ino: 32, allo_blina: 48, ph_ino: 29, ino: 16, soc_ph: 22 },
          '3Y': { nexcar_rwd: null, nexcar_bcj: null, sct_ino: 'NA', allo_blina: 48, ph_ino: 18, ino: 'NA', soc_ph: 13 },
        },
      },
      OS: {
        'Refractory + Relapsed': {
          '1Y': { nexcar_rwd: 65, nexcar_bcj: 64, sct_ino: 46, allo_blina: 74, ph_ino: 65, ino: 33, soc_ph: 52 },
          '2Y': { nexcar_rwd: 57, nexcar_bcj: 57, sct_ino: 42, allo_blina: 59, ph_ino: 48, ino: 23, soc_ph: 38 },
          '3Y': { nexcar_rwd: null, nexcar_bcj: null, sct_ino: 'NA', allo_blina: 54, ph_ino: 36, ino: 20, soc_ph: 28 },
        },
      },
    },
    keyInsight: {
      PFS: 'NexCAR19 demonstrates stronger sustained PFS durability in the combined refractory + relapsed B-ALL group versus strategy-based SOC comparators.',
      OS: 'NexCAR19 demonstrates stronger sustained OS durability in the combined refractory + relapsed B-ALL group versus strategy-based SOC comparators.',
    },
  },
};

const TIME_POINTS = ['1Y', '2Y' , '3Y' ]; // 3Y commented out per client request
const TIME_LABELS = { '1Y': '1 Year', '2Y': '2 Year', '3Y': '3 Year' };

// Strategy badge for B-ALL columns
const StrategyBadge = ({ strategy }) => {
  if (!strategy) return null;
  const isStrategy1 = strategy === 1;
  return (
    <span
      className={`inline-block mt-1 px-2 py-0.5 rounded text-[9px] font-bold ${
        isStrategy1 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
      }`}
    >
      {isStrategy1 ? 'Strategy 1*' : 'Strategy 2#'}
    </span>
  );
};

const SOCBenchmark = ({ indication = 'NHL' }) => {
  const [activeMetric, setActiveMetric] = useState('PFS');
  const socRef = useRef(null);
  const user = getStoredUser();
  const data = benchmarkingData[indication] || benchmarkingData['NHL'];
  const headers = data.headers;
  const groups = data.groups;
  const metricData = data.data[activeMetric];
  const colCount = headers.length;
  const refHeaderId = headers.find((h) => h.isRef)?.id;

  const metricLabel = activeMetric === 'PFS'
    ? 'Progression Free Survival (PFS)'
    : 'Overall Survival (OS)';

  const handleExportPNG = async () => {
    if (!socRef.current) return;
    const filename = generateExportFilename('SOC_Benchmark', { indication, analysisType: activeMetric });
    await exportElementAsPNG(socRef.current, filename);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden relative" ref={socRef}>
      {/* Export Button */}
      <ExportButton
        onExportPNG={handleExportPNG}
        userRole={user?.role}
        position="top-right"
        className="top-3 right-3"
      />

      {/* ── Card Header ── */}
      <div className="bg-gradient-to-r from-teal-50 to-blue-50 px-5 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Performance Benchmark</h2>
            <p className="text-sm text-teal-700 font-semibold mt-0.5">
              SOC Comparative Analysis – {metricLabel}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Year-on-year {activeMetric} durability across {indication === 'NHL' ? 'relapse/refractory' : 'combined refractory + relapsed'} groups
            </p>
          </div>

          {/* PFS / OS Toggle */}
          <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm border border-gray-200">
            {['PFS', 'OS'].map((metric) => (
              <button
                key={metric}
                onClick={() => setActiveMetric(metric)}
                className={`px-4 py-0.5 rounded-md text-xs font-bold transition-all ${
                  activeMetric === metric
                    ? 'bg-slate-800 text-white shadow'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {metric}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* ── Table ── */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-center">
            {/* Column Headers */}
            <thead>
              <tr className="border-b-2 border-slate-200">
                <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider py-3 px-3 w-[120px]">
                  r/r {indication === 'NHL' ? 'B-NHL' : 'B-ALL'}
                </th>
                <th className="text-[10px] font-bold text-slate-400 uppercase tracking-wider py-3 px-2 w-[60px]">
                  Metric
                </th>
                {headers.map((h) => (
                  <th
                    key={h.id}
                    className={`py-3 px-2 relative ${h.isRef ? 'bg-emerald-50/60' : ''}`}
                  >
                    {h.isRef && (
                      <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500 rounded-t" />
                    )}
                    <span className={`font-bold text-[11px] leading-tight block ${
                      h.isRef ? 'text-emerald-700' : 'text-slate-800'
                    }`}>
                      {h.name}
                    </span>
                    {h.sub && (
                      <span className={`text-[9px] block mt-0.5 ${
                        h.isRef ? 'text-emerald-600 font-medium' : 'text-slate-500'
                      }`}>
                        {h.sub}
                      </span>
                    )}
                    {!h.isRef && !h.sub && <StrategyBadge strategy={h.strategy} />}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {groups.map((group) => {
                const groupData = metricData[group.label];
                if (!groupData) return null;

                return TIME_POINTS.map((tp, tpIdx) => {
                  const rowValues = groupData[tp];
                  const refVal = rowValues[refHeaderId];

                  return (
                    <tr
                      key={`${group.label}-${tp}`}
                      className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${
                        tpIdx === TIME_POINTS.length - 1 ? 'border-b-2 border-slate-200' : ''
                      }`}
                    >
                      {/* Group label — only on first row of the group */}
                      {tpIdx === 0 ? (
                        <td
                          rowSpan={TIME_POINTS.length}
                          className="text-left text-[11px] font-semibold text-slate-700 px-3 py-2 align-middle border-r border-slate-100"
                        >
                          {group.label}
                        </td>
                      ) : null}

                      {/* Time Point label */}
                      <td className="text-[10px] font-bold text-slate-500 px-2 py-3 whitespace-nowrap">
                        {TIME_LABELS[tp]} {activeMetric}
                      </td>

                      {/* Value cells */}
                      {headers.map((h) => {
                        const val = rowValues[h.id];
                        const isEmpty = val === null || val === undefined; // renders dash "–"
                        const isNA = val === 'NA';                         // renders literal "NA"
                        const delta = !isEmpty && !isNA && !h.isRef && refVal != null
                          ? val - refVal
                          : null;

                        return (
                          <td
                            key={h.id}
                            className={`px-2 py-3 ${h.isRef ? 'bg-emerald-50/30' : ''}`}
                          >
                            {isEmpty ? (
                              <span className="text-xs text-slate-300 font-medium">–</span>
                            ) : isNA ? (
                              <span className="text-xs text-slate-400 font-semibold">NA</span>
                            ) : (
                              <div className="flex flex-col items-center gap-1.5">
                                <div className="flex items-center gap-1.5">
                                  <span className={`font-bold text-[13px] ${
                                    h.isRef ? 'text-emerald-700' : 'text-slate-700'
                                  }`}>
                                    {val}%
                                  </span>
                                  {delta !== null && (
                                    <span className={`text-[8px] px-1 py-0.5 rounded-md font-bold ${
                                      delta < 0
                                        ? 'bg-red-100 text-red-600'
                                        : delta > 0
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-slate-100 text-slate-500'
                                    }`}>
                                      {delta > 0 ? '+' : ''}{delta}%
                                    </span>
                                  )}
                                </div>
                                {/* Progress bar */}
                                <div className="w-16 bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      h.isRef ? 'bg-emerald-500' : 'bg-slate-400'
                                    }`}
                                    style={{ width: `${val}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>

        {/* B-ALL Protocol Strategy Definitions */}
        {indication === 'B-ALL' && (
          <div className="mt-6 mb-2 border border-slate-200 rounded-xl p-4 bg-slate-50/50">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">Protocol Strategy Definitions</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {/* Strategy 1 */}
              <div className="bg-white border border-blue-100 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded bg-blue-600 text-white text-[10px] font-black flex items-center justify-center flex-shrink-0">1</span>
                  <span className="text-sm font-bold text-blue-700 italic">Strategy 1: CAR-T → BMT (Bridge Therapy)</span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Used as remission induction (Salvage). Preferred for high-risk biology or when poor CAR-T persistence is expected. The primary objective is to induce a CR-MRD negative status prior to definitive Bone Marrow Transplant.
                </p>
              </div>
              {/* Strategy 2 */}
              <div className="bg-white border border-amber-100 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded bg-amber-500 text-white text-[10px] font-black flex items-center justify-center flex-shrink-0">2</span>
                  <span className="text-sm font-bold text-amber-700 italic">Strategy 2: Definitive Therapy</span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Employed for transplant-ineligible or older patients. Utilized when durable CAR-T persistence is clinically anticipated, or for refractory/MRD positive disease management as the final line.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-5 flex gap-4 pl-2 text-xs text-slate-500 border-b border-slate-100 pb-4">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
            Reference Value (NexCAR19)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-slate-400 inline-block" />
            Standard of Care (SOC)
          </span>
        </div>

        {/* Source Documentation */}
        <div className="mt-4 pl-2 mb-2">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
            Source Documentation ({indication})
          </h4>
          <ul className="text-xs text-slate-600 space-y-2 list-none">
            {headers.filter((h) => h.citation).map((h) => (
              <li key={`cit-${h.id}`} className="flex gap-2 leading-snug">
                <span className="font-semibold min-w-[180px] text-slate-700 flex-shrink-0">
                  {h.name}{h.sub ? ` (${h.sub})` : ''}:
                </span>
                <span>
                  {h.citation.includes('https://') ? (
                    <>
                      {h.citation.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                        part.match(/^https?:\/\//) ? (
                          <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline break-all">{part}</a>
                        ) : (
                          <span key={i}>{part}</span>
                        )
                      )}
                    </>
                  ) : (
                    h.citation
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer Note */}
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-[11px] text-amber-800">
            <span className="font-bold">Note:</span> Tali-cel comparison data is for patients infused up to December 2025, with last follow-up in April 2026.
            Comparator data are drawn from the cited publications and should be interpreted with caution given cross-study differences.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SOCBenchmark;
